/**
 * ActivityHeatmap — GitHub-style activity grid showing lifting days.
 * Shows the last 12 weeks of workout activity.
 */

import { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';

interface ActivityHeatmapProps {
  /** Array of ISO date strings when workouts occurred */
  workoutDates: string[];
  /** Number of weeks to display (default 12) */
  weeks?: number;
}

export default function ActivityHeatmap({ workoutDates, weeks = 12 }: ActivityHeatmapProps) {
  const { theme } = useTheme();
  const c = theme.colors;

  const { grid, monthLabels } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const totalDays = weeks * 7;

    // Build date->count map using local dates (not UTC) so workouts near
    // midnight appear on the correct calendar day in the user's timezone.
    const countMap: Record<string, number> = {};
    workoutDates.forEach(d => {
      const dt = new Date(d);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      countMap[key] = (countMap[key] || 0) + 1;
    });

    // Build grid columns (each column = 1 week, 7 rows)
    const cols: { date: string; count: number; dayOfWeek: number }[][] = [];
    const mLabels: { label: string; colIndex: number }[] = [];
    let lastMonth = -1;

    for (let w = weeks - 1; w >= 0; w--) {
      const col: { date: string; count: number; dayOfWeek: number }[] = [];
      for (let d = 0; d < 7; d++) {
        const daysAgo = w * 7 + (6 - d);
        const date = new Date(today.getTime() - daysAgo * 86400000);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        col.push({ date: key, count: countMap[key] || 0, dayOfWeek: date.getDay() });

        // Track month boundaries
        const month = date.getMonth();
        if (month !== lastMonth && d === 0) {
          lastMonth = month;
          mLabels.push({
            label: date.toLocaleDateString(undefined, { month: 'short' }),
            colIndex: cols.length,
          });
        }
      }
      cols.push(col);
    }

    return { grid: cols, monthLabels: mLabels };
  }, [workoutDates, weeks]);

  const getColor = (count: number) => {
    if (count === 0) return c.surfaceVariant;
    if (count === 1) return c.accent + '50';
    if (count === 2) return c.accent + '80';
    return c.accent;
  };

  const cellSize = 10;
  const gap = 2;

  return (
    <View style={styles.container}>
      {/* Month labels */}
      <View style={[styles.monthRow, { marginLeft: 18 }]}>
        {monthLabels.map((m, i) => (
          <Text
            key={i}
            style={[styles.monthLabel, { color: c.textTertiary, left: m.colIndex * (cellSize + gap) }]}
          >
            {m.label}
          </Text>
        ))}
      </View>

      <View style={styles.gridContainer}>
        {/* Day labels */}
        <View style={styles.dayLabels}>
          {['', 'M', '', 'W', '', 'F', ''].map((d, i) => (
            <Text key={i} style={[styles.dayLabel, { color: c.textTertiary, height: cellSize + gap }]}>
              {d}
            </Text>
          ))}
        </View>

        {/* Grid */}
        <View style={styles.grid}>
          {grid.map((col, ci) => (
            <View key={ci} style={styles.gridCol}>
              {col.map((cell, ri) => (
                <View
                  key={ri}
                  style={[styles.cell, {
                    width: cellSize,
                    height: cellSize,
                    backgroundColor: getColor(cell.count),
                    borderRadius: 2,
                  }]}
                />
              ))}
            </View>
          ))}
        </View>
      </View>

      {/* Legend */}
      <View style={styles.legend}>
        <Text style={[styles.legendText, { color: c.textTertiary }]}>Less</Text>
        {[0, 1, 2, 3].map(level => (
          <View
            key={level}
            style={[styles.legendCell, {
              backgroundColor: getColor(level),
              borderRadius: 2,
            }]}
          />
        ))}
        <Text style={[styles.legendText, { color: c.textTertiary }]}>More</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
  monthRow: {
    flexDirection: 'row',
    height: 14,
    position: 'relative',
  },
  monthLabel: {
    position: 'absolute',
    fontSize: 11,
    fontWeight: '600',
  },
  gridContainer: {
    flexDirection: 'row',
  },
  dayLabels: {
    width: 16,
    marginRight: 2,
  },
  dayLabel: {
    fontSize: 8,
    fontWeight: '600',
    textAlign: 'right',
    lineHeight: 12,
  },
  grid: {
    flexDirection: 'row',
    gap: 2,
  },
  gridCol: {
    gap: 2,
  },
  cell: {
    width: 10,
    height: 10,
  },
  legend: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 3,
    marginTop: 4,
  },
  legendCell: {
    width: 10,
    height: 10,
  },
  legendText: {
    fontSize: 11,
    fontWeight: '500',
  },
});
